/** @format */

import { useEffect, useState, useRef, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
	completeIdentityVerification,
	getAccountVerificationStatus,
	getIdentityVerificationStatus,
} from "../../../redux/actions/circleAction";
import { AppDispatch } from "../../../redux/store/store";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
	Box,
	IconButton,
	Paper,
	Snackbar,
	Tooltip,
	Typography,
	Button,
	CircularProgress,
	LinearProgress,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { selectCurrentUser } from "../../../redux/selectors/userSelector";
import { getUser } from "../../../redux/actions/userActions";
import { Alert, Link as MUILink } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { colors } from "../../../utility/types/constant";

// Polling configuration
const POLL_INTERVAL_MS = 3000; // Poll every 3 seconds
const MAX_POLL_ATTEMPTS = 40; // ~2 minutes; Stripe review regularly outlasts a 60s window

export default function IdentityVerifiedPage() {
	const [searchParams] = useSearchParams();
	const [error, setError] = useState("");
	const [isPhoneRedirect, setIsPhoneRedirect] = useState(false);
	const [secondaryVerified, setSecondaryVerified] = useState(false);
	const [isPolling, setIsPolling] = useState(false);
	const [pollAttempts, setPollAttempts] = useState(0);
	const [verificationComplete, setVerificationComplete] = useState(false);
	const startedRef = useRef(false);
	const cancelledRef = useRef(false);
	const successTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
	const userId = searchParams.get("userId") ?? "";
	const returnToken = searchParams.get("t") ?? "";
	const navigate = useNavigate();
	const dispatch = useDispatch<AppDispatch>();
	const [, setNotifyOpen] = useState(true);
	const [countDown, setCountdown] = useState(10);
	const userData = useSelector(selectCurrentUser);
	const COMPANY_URL = "https://fundsyfi.com";
	const PRO_FONT = "'Inter', 'Helvetica Neue', Arial, sans-serif";

	const [copied, setCopied] = useState(false);

	// Handle successful verification - start countdown to bank setup
	const handleVerificationSuccess = useCallback(() => {
		setVerificationComplete(true);
		setIsPolling(false);
		dispatch(getUser(userId));
		dispatch(getAccountVerificationStatus(userId));

		// Start countdown to redirect to bank account setup
		let sec = 10;
		setCountdown(sec);
		successTimerRef.current = setInterval(() => {
			sec -= 1;
			setCountdown(sec);

			if (sec <= 0) {
				if (successTimerRef.current) clearInterval(successTimerRef.current);
				setNotifyOpen(false);
				navigate(`/account-link/onboarding/refresh/${userId}`);
			}
		}, 1000);
	}, [dispatch, navigate, userId]);

	useEffect(
		() => () => {
			if (successTimerRef.current) clearInterval(successTimerRef.current);
		},
		[],
	);

	useEffect(() => {
		// Re-entering the effect (React strict mode) must not kill the in-flight loop.
		cancelledRef.current = false;

		if (!userId) {
			navigate("/", { replace: true });
			return;
		}

		if (startedRef.current) return;
		startedRef.current = true;

		const wait = (ms: number) =>
			new Promise<void>((resolve) => setTimeout(resolve, ms));

		const goToBanned = (details?: {
			bannedAt?: string;
			banReason?: string;
		}) =>
			navigate("/account-banned", {
				replace: true,
				state: { bannedAt: details?.bannedAt, banReason: details?.banReason },
			});

		const isBannedResult = (value: any) =>
			Boolean(value?.isBanned ?? value?.details?.isBanned);

		const failAndLeave = async (message: string) => {
			setIsPolling(false);
			setError(message);
			await wait(3000);
			if (!cancelledRef.current) {
				navigate(`/user/${userId}`, { replace: true });
			}
		};

		// This browser has no session, so only the return token can confirm the result.
		const pollPublicStatus = async () => {
			setIsPolling(false);
			setIsPhoneRedirect(true);

			if (!returnToken) return;

			for (let attempt = 1; attempt <= MAX_POLL_ATTEMPTS; attempt++) {
				if (cancelledRef.current) return;
				setPollAttempts(attempt);

				try {
					const status = await dispatch(
						getIdentityVerificationStatus({ userId, token: returnToken }),
					).unwrap();

					if (status?.verified) {
						setSecondaryVerified(true);
						return;
					}
				} catch {
					// Transient failure - keep polling until the attempt budget runs out.
				}

				await wait(POLL_INTERVAL_MS);
			}
		};

		const run = async () => {
			for (let attempt = 1; attempt <= MAX_POLL_ATTEMPTS; attempt++) {
				if (cancelledRef.current) return;
				setPollAttempts(attempt);

				try {
					const response = await dispatch(
						completeIdentityVerification({ userId }),
					).unwrap();

					if (isBannedResult(response)) {
						goToBanned(response);
						return;
					}

					if (!response?.pending) {
						handleVerificationSuccess();
						return;
					}

					setIsPolling(true);
				} catch (err: any) {
					if (isBannedResult(err)) {
						goToBanned(err);
						return;
					}

					// No session on this device - fall back to the token-gated status check.
					if (err?.httpStatus === 401) {
						await pollPublicStatus();
						return;
					}

					// Authenticated but forbidden (e.g. deleted account) - do not imply it is still processing.
					if (err?.httpStatus === 403) {
						await failAndLeave(
							err?.message || "We could not complete your verification.",
						);
						return;
					}

					setIsPolling(true);
				}

				await wait(POLL_INTERVAL_MS);
			}

			if (cancelledRef.current) return;

			setIsPolling(false);
			setError(
				"Verification is taking longer than expected. We'll notify you when it's complete.",
			);

			await wait(3000);

			if (!cancelledRef.current) {
				navigate(`/user/${userId}?verification=processing`, { replace: true });
			}
		};

		run();

		return () => {
			cancelledRef.current = true;
		};
	}, [userId, returnToken, dispatch, navigate, handleVerificationSuccess]);

	const handleCopy = async () => {
		try {
			await navigator.clipboard.writeText(COMPANY_URL);
			setCopied(true);
		} catch {
			const textArea = document.createElement("textarea");
			textArea.value = COMPANY_URL;
			document.body.appendChild(textArea);
			textArea.select();
			document.execCommand("copy");
			document.body.removeChild(textArea);
			setCopied(true);
		}
	};

	return (
		<>
			<Box
				component={motion.div}
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				exit={{ opacity: 0 }}
				sx={{
					position: "fixed",
					top: 0,
					left: 0,
					width: "100vw",
					height: "100vh",
					bgcolor: "background.paper",
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					justifyContent: "center",
					p: 2,
					textAlign: "center",
					zIndex: 1300,
				}}>
				<AnimatePresence>
					<motion.div
						key="checkmark"
						initial={{ scale: 0 }}
						animate={{ scale: 1 }}
						transition={{ type: "spring", stiffness: 260, damping: 20 }}>
						{isPhoneRedirect ?
							secondaryVerified ?
								<CheckCircleOutlineIcon
									color="success"
									sx={{ fontSize: 80, mb: 2 }}
								/>
								: returnToken ?
									<HourglassEmptyIcon color="info" sx={{ fontSize: 80, mb: 2 }} />
									: <PhoneIphoneIcon color="success" sx={{ fontSize: 80, mb: 2 }} />
							: isPolling ?
								<HourglassEmptyIcon color="info" sx={{ fontSize: 80, mb: 2 }} />
								: verificationComplete ?
									<CheckCircleOutlineIcon
										color="success"
										sx={{ fontSize: 80, mb: 2 }}
									/>
									: error ?
										<CheckCircleOutlineIcon
											color="warning"
											sx={{ fontSize: 80, mb: 2 }}
										/>
										: <CircularProgress size={80} sx={{ mb: 2 }} />}
					</motion.div>
				</AnimatePresence>

				{isPhoneRedirect ?
					secondaryVerified ?
						<>
							<Typography variant="h4" gutterBottom>
								Verification Complete!
							</Typography>
							<Typography
								variant="body1"
								sx={{
									mb: 3,
									fontSize: "1.25rem",
									lineHeight: 1.6,
									maxWidth: 600,
								}}>
								Your identity has been verified. You can continue on your
								original device, or sign in here to finish setting up your
								payout account.
							</Typography>
							<Button
								variant="contained"
								onClick={() => navigate("/circle/sign-in")}
								sx={{ mt: 1 }}>
								Continue on This Device
							</Button>
						</>
						: returnToken ?
							<>
								<Typography variant="h4" gutterBottom>
									Documents Submitted
								</Typography>
								<Typography
									variant="body1"
									sx={{
										mb: 2,
										fontSize: "1.25rem",
										lineHeight: 1.6,
										maxWidth: 600,
									}}>
									Stripe is reviewing your documents. Keep this page open, we'll
									confirm as soon as the result arrives.
								</Typography>
								<Box sx={{ width: "100%", maxWidth: 400, mb: 3 }}>
									<LinearProgress
										variant="determinate"
										value={(pollAttempts / MAX_POLL_ATTEMPTS) * 100}
										sx={{ height: 8, borderRadius: 4 }}
									/>
								</Box>
								<Alert severity="info" sx={{ maxWidth: 600, p: 2 }}>
									<Typography sx={{ fontSize: "1rem" }}>
										You do not need to submit your ID again. If you started on
										another device, you can also continue there.
									</Typography>
								</Alert>
							</>
							: <>
								<Typography variant="h4" gutterBottom>
									Photo Upload Complete!
								</Typography>
								<Typography
									variant="body1"
									sx={{
										mb: 3,
										fontSize: "1.25rem",
										lineHeight: 1.6,
										maxWidth: 600,
									}}>
									Your documents have been submitted successfully. Please return to
									your <strong>original device</strong> (computer or tablet) where
									you started the verification to continue setting up your account.
								</Typography>
								<Alert
									severity="info"
									sx={{
										mb: 3,
										maxWidth: 600,
										p: 2,
									}}>
									<Typography sx={{ fontSize: "1rem" }}>
										You can safely close this page on your phone. The verification
										will continue automatically on your other device.
									</Typography>
								</Alert>
								<Button
									variant="outlined"
									onClick={() => navigate("/circle/sign-in")}
									sx={{ mt: 2 }}>
									Sign In on This Device Instead
								</Button>
							</>

					: isPolling ?
						<>
							<Typography variant="h4" gutterBottom>
								Verifying Your Identity...
							</Typography>
							<Typography
								variant="body1"
								sx={{
									mb: 2,
									fontSize: "1.25rem",
									lineHeight: 1.6,
									maxWidth: 600,
								}}>
								Stripe is processing your documents. This usually takes a few
								seconds.
							</Typography>
							<Box sx={{ width: "100%", maxWidth: 400, mb: 3 }}>
								<LinearProgress
									variant="determinate"
									value={(pollAttempts / MAX_POLL_ATTEMPTS) * 100}
									sx={{ height: 8, borderRadius: 4 }}
								/>
								<Typography
									variant="caption"
									color="text.secondary"
									sx={{ mt: 1 }}>
									Checking verification status... (Attempt {pollAttempts}/
									{MAX_POLL_ATTEMPTS})
								</Typography>
							</Box>
							<Alert severity="info" sx={{ maxWidth: 500 }}>
								<Typography>
									Please wait while we confirm your identity verification with
									Stripe.
								</Typography>
							</Alert>
						</>
						: <>
							<Typography variant="h4" gutterBottom>
								{error ? "Verification Processing" : "Verification Successful!"}
							</Typography>
							{error ?
								<Typography
									variant="body1"
									color="warning.main"
									sx={{
										mb: 3,
										fontSize: "1.25rem",
										lineHeight: 1.6,
									}}>
									{error}
								</Typography>
								: <Typography
									variant="body1"
									sx={{
										mb: 3,
										fontSize: "1.25rem",
										lineHeight: 1.6,
									}}>
									You'll be redirected to Stripe to securely enter your bank
									details for future payout processing in{" "}
									<Box component="span" fontWeight="bold">
										{countDown}
									</Box>{" "}
									seconds…
								</Typography>
							}

							<Alert
								severity="info"
								sx={{
									mb: 3,
									maxWidth: 720,
									p: 3,
									"& .MuiAlert-message": { p: 0, width: "100%" },
									borderRadius: 2,
								}}>
								<Typography
									component="div"
									sx={{
										fontFamily: PRO_FONT,
										fontSize: { xs: 18, sm: 20, md: 22, lg: 24 },
										fontWeight: 600,
										lineHeight: 1.5,
									}}>
									Personal bank accounts are fine. Stripe may label some fields as
									“business” just enter your own details.
									<br />
									If Stripe asks for a website URL, use:&nbsp;
									<MUILink
										href={COMPANY_URL}
										target="_blank"
										rel="noopener noreferrer"
										sx={{ fontFamily: PRO_FONT, fontWeight: 500 }}>
										{COMPANY_URL}
									</MUILink>
									<Tooltip title="Copy URL">
										<IconButton
											size="small"
											onClick={handleCopy}
											sx={{ ml: 1, verticalAlign: "middle" }}>
											<ContentCopyIcon fontSize="inherit" />
										</IconButton>
									</Tooltip>
								</Typography>
							</Alert>
						</>
				}
				<Snackbar
					open={copied}
					autoHideDuration={2500}
					onClose={() => setCopied(false)}
					message="URL copied to clipboard"
				/>
			</Box>

			{/* {userData?.isVerified === true && (
        <SubscriptionModal
          open={subscriptionOpen}
          handleClose={() => setSubscriptionOpen(false)}
        />
      )} */}
		</>
	);
}
